/**
 * Zgłoszenia ubezpieczeniowe: ZUS ZUA i ZUS ZWUA (Etap 14).
 *
 * Płatnik ma obowiązek zgłosić osobę do ubezpieczeń w ciągu 7 dni od powstania
 * obowiązku i wyrejestrować ją w ciągu 7 dni od jego ustania (art. 36 ust. 4
 * i ust. 11 ustawy o systemie ubezpieczeń społecznych; poradnik ZUS „Ogólne
 * zasady wypełniania i korygowania dokumentów ubezpieczeniowych", rozdział III
 * pkt 3.2 i 3.3). Zgłoszenie nie jest zestawieniem miesiąca — to zdarzenie
 * jednej osoby, więc ma własny plik KEDU i własny termin.
 */
import type { ContractType } from './enums'

/**
 * Rodzaje dokumentów zgłoszeniowych, które wystawiamy:
 *  - **ZUA** — zgłoszenie do ubezpieczeń społecznych i zdrowotnego,
 *  - **ZZA** — zgłoszenie TYLKO do ubezpieczenia zdrowotnego (Etap 25); dotyczy
 *    osoby, która obowiązek społeczny ma z innego tytułu, np. zleceniobiorcy
 *    zatrudnionego gdzie indziej na etacie,
 *  - **ZWUA** — wyrejestrowanie,
 *  - **ZIUA** — zgłoszenie zmiany DANYCH IDENTYFIKACYJNYCH osoby ubezpieczonej
 *    (Etap 25): numeru PESEL, nazwiska, imienia albo daty urodzenia,
 *  - **ZCNA** — zgłoszenie danych o CZŁONKU RODZINY dla celów ubezpieczenia
 *    zdrowotnego albo jego utraty uprawnień (Etap 30). Termin liczy się od dnia,
 *    w którym ubezpieczony poinformował płatnika — nie od zdarzenia (ADR-0031).
 */
export const ZUS_REGISTRATION_KINDS = ['zua', 'zza', 'zwua', 'ziua', 'zcna'] as const
export type ZusRegistrationKind = (typeof ZUS_REGISTRATION_KINDS)[number]

/**
 * Zakres ubezpieczenia z tytułu tej umowy. Decyduje o formularzu: pełny zakres
 * idzie na ZUA, tylko zdrowotne na ZZA (art. 36 ustawy o systemie ubezpieczeń
 * społecznych; poradnik ZUS „Ogólne zasady…", rozdział I pkt 3).
 */
export const INSURANCE_SCOPES = ['pelne', 'tylko_zdrowotne'] as const
export type InsuranceScope = (typeof INSURANCE_SCOPES)[number]

/**
 * Dane identyfikacyjne ubezpieczonego — blok III (poprzednie) i IV (nowe)
 * formularza ZUS ZIUA. Zmiana KTÓREGOKOLWIEK z tych pól wymaga zgłoszenia.
 */
export interface ZusIdentity {
  readonly pesel: string | null
  readonly lastName: string
  readonly firstName: string
  readonly birthDate: string | null
}

/** Pola identyfikacyjne, których zmianę zgłasza się na ZUS ZIUA. */
export const ZUS_IDENTITY_FIELDS = ['pesel', 'lastName', 'firstName', 'birthDate'] as const
export type ZusIdentityField = (typeof ZUS_IDENTITY_FIELDS)[number]

/** Które pola identyfikacyjne różnią się między stanem poprzednim a nowym. */
export function changedIdentityFields(
  previous: ZusIdentity,
  next: ZusIdentity,
): readonly ZusIdentityField[] {
  return ZUS_IDENTITY_FIELDS.filter((field) => (previous[field] ?? '') !== (next[field] ?? ''))
}

/**
 * Tryb dokumentu zgłoszeniowego — blok I „Dane organizacyjne" (Etap 31,
 * ADR-0032).
 *
 * Formularz ZUS ZUA (i ZZA) ma w bloku I dwa pola: „zgłoszenie do ubezpieczeń"
 * (pole 01) oraz „zgłoszenie zmiany / korekty danych" (pole 02) z wartością
 * 1 = zmiana albo 2 = korekta. Różnica nie jest kosmetyczna:
 *
 *  - **zmiana** to nowy stan faktyczny (przeprowadzka, nowe nazwisko),
 *  - **korekta** to poprawienie tego, co było błędne od początku.
 *
 * ZWUA ma w bloku I pola logiczne: 01 wyrejestrowanie, 02 korekta. ZIUA ma
 * jedno pole z wartością 1 albo 2 — i dlatego ten sam formularz służy do zmiany
 * nazwiska po ślubie i do poprawienia literówki.
 */
export const ZUS_REGISTRATION_MODES = ['zgloszenie', 'zmiana', 'korekta'] as const
export type ZusRegistrationMode = (typeof ZUS_REGISTRATION_MODES)[number]

/**
 * Co było błędne w przekazanym zgłoszeniu. Zakres decyduje o DRODZE korekty,
 * a nie o treści dokumentu — poradniki ZUS („ZUS ZUA… Jak wypełnić
 * i skorygować", rozdział „Jak skorygować zgłoszenie", oraz odpowiedni rozdział
 * poradnika ZUS ZWUA) wskazują dla każdej grupy pól inną drogę.
 */
export const REGISTRATION_CORRECTION_SCOPES = [
  /** Dane ewidencyjne i adresowe (bloki IV oraz XI–XIII formularza ZUA). */
  'dane_ewidencyjne',
  /** Kod tytułu ubezpieczenia ze słownika (blok V formularza ZUA). */
  'kod_tytulu',
  /** Schemat ubezpieczeń albo data powstania obowiązku (bloki VI–IX). */
  'schemat_lub_data',
  /** Data wyrejestrowania, kod przyczyny albo blok V formularza ZWUA. */
  'dane_wyrejestrowania',
] as const
export type RegistrationCorrectionScope = (typeof REGISTRATION_CORRECTION_SCOPES)[number]

/**
 * Droga korekty: jeden dokument w trybie korekty albo PARA dokumentów —
 * wyrejestrowanie błędnego zgłoszenia i zgłoszenie od nowa.
 *
 * Ta druga droga zaskakuje, więc warto ją nazwać wprost: gdy w zgłoszeniu jest
 * błędny kod tytułu ubezpieczenia ze słownika albo błędny schemat ubezpieczeń,
 * ZUS każe wyrejestrować osobę **z datą powstania obowiązku** (a nie z dnia
 * następnego po ostatnim dniu ubezpieczenia, jak przy zwykłym ustaniu)
 * i zgłosić ją ponownie z tą samą datą, tylko z prawidłowymi danymi.
 */
export const CORRECTION_PATHS = ['korekta', 'wyrejestrowanie_i_zgloszenie'] as const
export type CorrectionPath = (typeof CORRECTION_PATHS)[number]

export function correctionPath(
  kind: ZusRegistrationKind,
  scope: RegistrationCorrectionScope,
): CorrectionPath | null {
  if (kind === 'zwua') {
    /* Poradnik ZUS ZWUA: data, kod przyczyny i blok V idą trybem korekty. */
    return scope === 'dane_wyrejestrowania' ? 'korekta' : null
  }
  if (kind !== 'zua' && kind !== 'zza') return null
  if (scope === 'dane_ewidencyjne') return 'korekta'
  if (scope === 'kod_tytulu' || scope === 'schemat_lub_data') {
    return 'wyrejestrowanie_i_zgloszenie'
  }
  return null
}

/**
 * Przyczyna wyrejestrowania w korekcie: „inna" (kod 600). Nie ma tu ustania
 * tytułu — jest anulowanie błędnego zgłoszenia, więc kod 100 („ustanie tytułu")
 * byłby nieprawdą wobec ZUS-u.
 */
export const CORRECTION_DEREGISTRATION_REASON: ZusDeregistrationReason = 'inna'

/** Stan dokumentu: do wystawienia, wystawiony, przekazany, z zastrzeżeniem. */
export const ZUS_REGISTRATION_STATUSES = ['todo', 'gen', 'sent', 'err'] as const
export type ZusRegistrationStatus = (typeof ZUS_REGISTRATION_STATUSES)[number]

/**
 * Kod przyczyny wyrejestrowania ubezpieczonego (blok IV pole 03 formularza
 * ZUS ZWUA; wykaz z poradnika ZUS „Ogólne zasady…", rozdział VII pkt VII).
 * Wystawiamy tylko te, które wynikają z faktów w aktach — pozostałych
 * (700 zawieszenie działalności) ten produkt nie zna.
 */
export const ZUS_DEREGISTRATION_REASONS = {
  /** Ustanie tytułu do ubezpieczeń — koniec umowy. */
  ustanie_tytulu: '100',
  /** Zgon osoby ubezpieczonej. */
  zgon: '500',
  /** Inna przyczyna wyrejestrowania. */
  inna: '600',
  /** Przejście zakładu pracy na innego pracodawcę (art. 23[1] k.p.). */
  przejscie_zakladu: '800',
} as const
export type ZusDeregistrationReason = keyof typeof ZUS_DEREGISTRATION_REASONS

/**
 * Oddziały wojewódzkie NFZ z kodami (poradnik ZUS „Ogólne zasady…", rozdział
 * VII pkt V). Kod wchodzi do bloku VII formularza ZUS ZUA — bez niego Płatnik
 * nie przyjmie zgłoszenia, więc jest to słownik wydania, nie dane firmy.
 */
export const NFZ_BRANCHES = [
  { code: '01R', name: 'Dolnośląski OW NFZ we Wrocławiu' },
  { code: '02R', name: 'Kujawsko-Pomorski OW NFZ w Bydgoszczy' },
  { code: '03R', name: 'Lubelski OW NFZ w Lublinie' },
  { code: '04R', name: 'Lubuski OW NFZ w Zielonej Górze' },
  { code: '05R', name: 'Łódzki OW NFZ w Łodzi' },
  { code: '06R', name: 'Małopolski OW NFZ w Krakowie' },
  { code: '07R', name: 'Mazowiecki OW NFZ w Warszawie' },
  { code: '08R', name: 'Opolski OW NFZ w Opolu' },
  { code: '09R', name: 'Podkarpacki OW NFZ w Rzeszowie' },
  { code: '10R', name: 'Podlaski OW NFZ w Białymstoku' },
  { code: '11R', name: 'Pomorski OW NFZ w Gdańsku' },
  { code: '12R', name: 'Śląski OW NFZ w Katowicach' },
  { code: '13R', name: 'Świętokrzyski OW NFZ w Kielcach' },
  { code: '14R', name: 'Warmińsko-Mazurski OW NFZ w Olsztynie' },
  { code: '15R', name: 'Wielkopolski OW NFZ w Poznaniu' },
  { code: '16R', name: 'Zachodniopomorski OW NFZ w Szczecinie' },
] as const

export type NfzBranchCode = (typeof NFZ_BRANCHES)[number]['code']

/** Nazwa oddziału dla kodu; `null`, gdy kod nie jest z wykazu ZUS. */
export function nfzBranchName(code: string | null): string | null {
  if (code === null) return null
  return NFZ_BRANCHES.find((branch) => branch.code === code)?.name ?? null
}

/** Czy tekst jest kodem oddziału NFZ z wykazu (a nie dowolnym opisem). */
export function isNfzBranchCode(value: string | null | undefined): boolean {
  return value !== null && value !== undefined && nfzBranchName(value) !== null
}

/** Zastrzeżenia zgłoszenia — brak danych, których nie wolno zgadnąć. */
export const REGISTRATION_ISSUE_KINDS = [
  /* ZCNA (Etap 30): członek rodziny bez identyfikatora, daty albo kodu. */
  'brak_identyfikatora_czlonka',
  'brak_daty_urodzenia_czlonka',
  'brak_kodu_pokrewienstwa',
  /** Brak numeru PESEL w aktach — bez niego nie ma identyfikacji ubezpieczonego. */
  'brak_pesel',
  /** Brak daty urodzenia — formularz wymaga jej w bloku danych osoby. */
  'brak_daty_urodzenia',
  /** Brak kodu oddziału NFZ (blok VII pole 02 formularza ZUS ZUA). */
  'brak_kodu_nfz',
  /**
   * Brak kodu wykonywanego zawodu. Od 16 maja 2021 r. zgłoszenie ZUS ZUA
   * wymaga sześcioznakowego kodu z klasyfikacji zawodów i specjalności — nie
   * da się go wyprowadzić ze stanowiska wpisanego słowami.
   */
  'brak_kodu_zawodu',
  /** Brak adresu zameldowania w aktach — bloku XI nie da się wypełnić. */
  'brak_adresu',
  /**
   * Zleceniobiorca przed 26. urodzinami (#8): jeśli był uczniem albo studentem,
   * obowiązek ubezpieczeń powstał dopiero z dniem utraty statusu (art. 6 ust. 4
   * u.s.u.s.), a flaga studenta w aktach nie ma daty. Nie blokuje — każe
   * sprawdzić datę powstania obowiązku.
   */
  'sprawdz_date_obowiazku',
  /**
   * Osoba jest zgłoszona do ZUS (przekazane ZUA albo ZZA), a akta nie dają już
   * tytułu do ubezpieczeń u płatnika — np. zaznaczono status studenta (#8).
   * Wyrejestrowania nie zgadujemy, bo nie znamy daty: trzeba je wystawić albo
   * poprawić zgłoszenie (ADR-0032).
   */
  'brak_tytulu_zgloszony',
  /**
   * Dokument trafił do pobranego pliku KEDU, a nie wynika już z akt (np. po
   * zaznaczeniu statusu studenta). Mógł pójść do ZUS-u, więc nie znika po
   * cichu: trzeba go oznaczyć jako przekazany albo wyjaśnić (przegląd Fali 2).
   */
  'nie_wynika_z_akt',
  /** Firma bez NIP albo REGON — płatnika nie da się zidentyfikować. */
  'brak_danych_platnika',
  /**
   * Blok V formularza ZUS ZWUA (rozwiązanie stosunku pracy) wymaga trybu
   * i podstawy prawnej rozwiązania. Bez przepływu offboardingu ich nie znamy.
   */
  'brak_trybu_rozwiazania',
  /**
   * ZIUA bez różnicy w danych identyfikacyjnych — nie ma czego zgłaszać.
   * Powstaje, gdy wniosek o zmianę danych zatwierdzono, ale wartość „po" jest
   * taka sama jak „przed" (Etap 25).
   */
  'brak_zmiany_danych',
] as const
export type RegistrationIssueKind = (typeof REGISTRATION_ISSUE_KINDS)[number]

export interface RegistrationIssue {
  readonly kind: RegistrationIssueKind
  readonly params: Readonly<Record<string, string | number>>
  /** `true` = dokument nie powstaje; `false` = powstaje, ale z zastrzeżeniem. */
  readonly blocking: boolean
}

/** Adres w rozbiciu, którego wymaga formularz — nie jeden ciąg znaków. */
export interface ZusAddress {
  readonly postalCode: string
  readonly city: string
  readonly street: string | null
  readonly houseNo: string
  readonly flatNo: string | null
}

/** Schemat podlegania ubezpieczeniom społecznym (blok VI formularza ZUS ZUA). */
export interface SocialInsuranceScheme {
  readonly pension: boolean
  readonly disability: boolean
  readonly sickness: boolean
  readonly accident: boolean
}

/** Wiersz listy zgłoszeń na ekranie — bez numeru PESEL. */
export interface ZusRegistrationView {
  readonly id: string | null
  readonly kind: ZusRegistrationKind
  readonly employeeId: string
  readonly employeeName: string
  readonly employeeNo: number | null
  /** Kod tytułu ubezpieczenia; ZIUA go nie ma (Etap 25). */
  readonly insuranceCode: string | null
  /** ZUA: data powstania obowiązku. ZWUA: data wyrejestrowania. */
  readonly effectiveOn: string
  /** Ustawowy termin przekazania: 7 dni od zdarzenia. */
  readonly deadline: string
  readonly status: ZusRegistrationStatus
  readonly reasonCode: string | null
  readonly hasPesel: boolean
  /**
   * ZIUA: pola identyfikacyjne, które się zmieniły, oraz stan przed i po.
   * Pozostałe rodzaje mają tu `null` — dokument dotyczy zgłoszenia, nie zmiany.
   */
  readonly identityChange: {
    readonly fields: readonly ZusIdentityField[]
    readonly previous: ZusIdentity
    readonly next: ZusIdentity
  } | null
  readonly issues: readonly RegistrationIssue[]
  readonly generatedAt: string | null
  readonly sentAt: string | null
}

export interface ZusRegistrationsResponse {
  readonly items: readonly ZusRegistrationView[]
  /** Ile pozycji czeka na wystawienie (stan `todo`). */
  readonly pendingCount: number
  /** Ile pozycji jest po ustawowym terminie. */
  readonly overdueCount: number
  readonly canRun: boolean
}

/** Numer domu i lokalu mieszczą się w typie `t_NrLokaluDomu_7` schematu KEDU. */
export const ADDRESS_NUMBER_MAX = 7

/** Ulica z numerem domu i lokalu w rozbiciu, którego wymaga formularz. */
export interface StreetLine {
  readonly street: string | null
  readonly houseNo: string
  readonly flatNo: string | null
}

/*
 * Numer domu: cyfry z literą („12A", także „64 A"), albo numer podwójny
 * z łącznikiem („12-14"). Litera po spacji nie może być znacznikiem lokalu
 * („m", „lok"), bo „Wiejska 5 m" to lokal bez numeru, a nie dom „5m".
 */
const HOUSE_NUMBER = String.raw`\d+(?:[A-Za-z]|\s(?![mMlL](?:\b|\.))[A-Za-z](?![A-Za-z.]))?(?:\s?-\s?\d+[A-Za-z]?)?`
/* Lokal: „/9", „/ 9", „m. 9", „m 9", „lok. 9", „lokal 9" — także po przecinku. */
const FLAT_NUMBER = String.raw`(?:\s*\/\s*|\s*,?\s*(?:m|lok|lokal)\.?\s*)(\d+[A-Za-z]?)`
/* Numer domu z ukośnikiem („84/92"), gdy lokal wskazuje JAWNY znacznik: „84/92 m. 5". */
const SLASHED_HOUSE = String.raw`(\d+[A-Za-z]?\/\d+[A-Za-z]?)\s*,?\s*(?:m|lok|lokal)\.?\s*(\d+[A-Za-z]?)`
const NUMBERS_ONLY = new RegExp(`^(${HOUSE_NUMBER})(?:${FLAT_NUMBER})?$`, 'i')
const NUMBERS_SLASHED = new RegExp(`^${SLASHED_HOUSE}$`, 'i')
const WITH_SPACE = new RegExp(`^(.*?)\\s+(${HOUSE_NUMBER})(?:${FLAT_NUMBER})?$`, 'i')
const WITH_SPACE_SLASHED = new RegExp(`^(.*?)\\s+${SLASHED_HOUSE}$`, 'i')
const STREET_PREFIX = /^(ul\.|al\.|pl\.|os\.)\s*/i
const FLAT_MARKER_END = /(?:^|\s)(?:m|lok|lokal)\.?$/i
/* Nazwa kończąca się liczbą albo znacznikiem lokalu — nie wiemy, gdzie jest numer domu. */
const AMBIGUOUS_END = /(?:^|\s)(?:\d+[A-Za-z]?|m|lok|lokal)\.?$/i

function streetLine(name: string, houseNo: string, flatNo: string | undefined): StreetLine | null {
  /* „64 A" → „64A", „12 - 14" → „12-14": formularz ma jedno pole bez spacji. */
  const house = houseNo.replace(/\s+/g, '')
  if (house.length > ADDRESS_NUMBER_MAX) return null
  if (flatNo !== undefined && flatNo.length > ADDRESS_NUMBER_MAX) return null
  const street = name.replace(STREET_PREFIX, '').trim()
  return { street: street.length > 0 ? street : null, houseNo: house, flatNo: flatNo ?? null }
}

/** Sam numer (z lokalem albo bez) — przy przecinku i przy miejscowości bez ulic. */
function numbersOnly(text: string): { house: string; flat: string | undefined } | null {
  const slashed = NUMBERS_SLASHED.exec(text)
  if (slashed) return { house: slashed[1]!, flat: slashed[2] }
  const plain = NUMBERS_ONLY.exec(text)
  return plain ? { house: plain[1]!, flat: plain[2] } : null
}

/**
 * Ulica z numerem domu i lokalu z jednego pola akt (#7). Rozpoznaje lokal
 * zapisany jako „/9", „m. 9", „lok. 9" i „lokal 9", numer podwójny „12-14"
 * i dom z ukośnikiem przed jawnym znacznikiem lokalu („84/92 m. 5"). Sam
 * numer bez ulicy (miejscowość bez ulic) daje ulicę pustą — blok XI ją
 * dopuszcza. Przecinek przed numerem domu jest JAWNYM separatorem:
 * „ul. Dywizjonu 303, 12 m. 4" daje ulicę „Dywizjonu 303" i dom 12. Zapisu
 * niejednoznacznego — gdy przy separatorze ze spacji nazwa ulicy kończy się
 * liczbą albo znacznikiem lokalu („Targowa 20 9") — NIE rozbijamy: `null`,
 * a dokument dostaje zastrzeżenie z podpowiedzią zapisu (ADR-0017 §2: czego
 * nie wiemy, tego nie wystawiamy).
 */
export function parseStreetLine(line: string | null | undefined): StreetLine | null {
  if (line === null || line === undefined) return null
  const text = line.trim().replace(/\s+/g, ' ')
  if (text.length === 0) return null

  const bare = numbersOnly(text)
  if (bare) return streetLine('', bare.house, bare.flat)

  for (let index = text.indexOf(','); index >= 0; index = text.indexOf(',', index + 1)) {
    const name = text.slice(0, index).trim()
    const numbers = numbersOnly(text.slice(index + 1).trim())
    if (numbers && name.length > 0 && !FLAT_MARKER_END.test(name)) {
      return streetLine(name, numbers.house, numbers.flat)
    }
  }

  const slashed = WITH_SPACE_SLASHED.exec(text)
  const spaced = slashed ?? WITH_SPACE.exec(text)
  if (!spaced) return null
  const name = spaced[1]!
  if (AMBIGUOUS_END.test(name.replace(STREET_PREFIX, '').trim())) return null
  return streetLine(name, spaced[2]!, spaced[3])
}

/** Formularz zgłoszenia, który wynika z umowy: pełny zakres albo tylko zdrowotne. */
export type RegistrationForm = 'zua' | 'zza'

/**
 * JEDNA reguła tytułu do ubezpieczeń dla rejestru zgłoszeń, dla bramki
 * członka rodziny i dla walidacji profilu (#8, grupa 166). Ta sama, którą liczy
 * silnik płac:
 *  - brak umowy, B2B i umowa o dzieło nie są tytułem z art. 6 ust. 1 u.s.u.s.
 *    (ADR-0047) — `null`,
 *  - zlecenie ucznia albo studenta do 26 lat nie daje ubezpieczeń społecznych
 *    ani zdrowotnego (art. 6 ust. 4 u.s.u.s., art. 66 u.ś.o.z.) — `null`,
 *  - zlecenie o zakresie „tylko zdrowotne" to ZZA (ADR-0026 §1),
 *  - reszta, a umowa o pracę zawsze — ZUA. Flaga studenta i zakres dotyczą
 *    wyłącznie zlecenia, tak jak w silniku.
 */
export function registrationFormFor(facts: {
  readonly contractType: ContractType | null
  readonly mandateStudentExempt?: boolean | null
  readonly insuranceScope?: InsuranceScope | null
}): RegistrationForm | null {
  const { contractType } = facts
  if (contractType === null || contractType === 'b2b' || contractType === 'dzielo') return null
  if (contractType === 'zlecenie') {
    if (facts.mandateStudentExempt === true) return null
    return facts.insuranceScope === 'tylko_zdrowotne' ? 'zza' : 'zua'
  }
  return 'zua'
}
